import { getCalApi } from '@calcom/embed-react';
import posthog from 'posthog-js';
import { useCallback } from 'react';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useThemeStore } from '@/Domain/store/theme.store';

// The only Cal.com booking link used in-app; keep new call sites pointing here, not at raw URLs.
export const CAL_LINK = {
  productDemo: 'team/quickchatai/product-demo',
} as const;

// Cal's `on` stacks callbacks, so register the booking-success listener once per page load.
let bookingListenerRegistered = false;

// Opens a Cal.com booking in an in-app modal (theme-synced) instead of an external tab.
// The static `getCalApi` import is load-bearing: the `await import(...)` variant makes Vite emit
// modulepreload hints that resolve to the app origin, 404 in production, and reject on first click.
// `source` labels the entry point for analytics (the in-app embed emits no event of its own otherwise).
// Uses the posthog singleton (not usePostHog): the booking listener is registered once, so a hook
// value would freeze on the first opener's render — the singleton always resolves the live client.
export const useCalModal = () => {
  const getResolvedTheme = useThemeStore((state) => state.getResolvedTheme);
  return useCallback(
    async (calLink: (typeof CAL_LINK)[keyof typeof CAL_LINK], source?: string) => {
      const cal = await getCalApi();
      cal('ui', { theme: getResolvedTheme() });
      if (!bookingListenerRegistered) {
        bookingListenerRegistered = true;
        cal('on', {
          action: 'bookingSuccessful',
          callback: () => posthog.capture(PostHogEventEnum.DemoCallBooked),
        });
      }
      posthog.capture(PostHogEventEnum.DemoModalOpened, { cal_link: calLink, source });
      cal('modal', { calLink });
    },
    [getResolvedTheme],
  );
};
